// Library Imports
import { Radio, message } from 'antd';
import styled from 'styled-components';
import store, { RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { useRef, useEffect, Suspense, useState } from 'react';
// Local Imports
import ListItem from './ListItem';
import { getQueryParam } from 'utils';
import { findDropAreaData } from '../utils';
import useQuery from '../../../hooks/useQuery';
import Input from 'modules/Common/Components/Input';
import { ALERT_MESSAGES } from 'constants/messages';
import Message from 'modules/Common/Components/Message';
import { DEFAULT_CAMPAIGN_IMAGE } from 'constants/types';
import EmptyTabInfo from 'modules/Content/Shared/EmptyTabInfo';
import InfiniteLoader from 'modules/Common/Components/InfiniteLoader';
import AutomationSkeleton from 'modules/Content/Shared/AutomationSkeleton';
import { ParamsObjectType, ControlNameEnum, IListData } from '../interfaces';
import SentEmailSearch from '../../Campaigns/Emails/Components/SentEmailSearch';
import { createNewListInWorkflow } from 'modules/Campaigns/Audience/Lists/actions';
import { AUTOMATION_LISTS, AUTOMATION_LISTS_TRIGGER, AUTOMATION_USER_WORKFLOWS } from 'constants/types/automationTypes';
import {
  triggerListsAPI,
  resetTriggerWorkflowLists,
  updateWorkflowTriggerList,
  addActionModal,
  clearDropArea,
  listsAPI,
  setSelectedlistIdsAction,
  updateListSearchTermInStore,
  resetWorkflowLists,
  editNode,
  addNode,
  setSelectedListsAction,
} from '../actions';
// Icon Imports
import TickNormal from '../../../assets/img/icons/tickNormal.svg';
import crossNormal from '../../../assets/img/icons/crossNormal.svg';

interface IAudienceList {
  id: string;
  activeKey: string;
  isNewList: boolean;
  isFiltered?: boolean;
  setErrorFields?: any;
  isWorkflow?: boolean;
  isEditMode?: boolean;
  errorFields?: string[];
  isFormValid?: () => any;
  selectedListId?: number;
  isActionModal?: boolean;
  createNewListName: string;
  resetSelectedLists: boolean;
  loadingEmailUpdate?: boolean;
  changeHandler: (id: number) => void;
  setIsNewList: (value: boolean) => void;
  enableSaveButton: (val: boolean) => void;
  setCreateNewlistName: (value: string) => void;
  setResetSelectedLists: (value: boolean) => void;
}

const AudienceList: React.FC<IAudienceList> = (props: IAudienceList) => {
  // Props
  const {
    id,
    isNewList,
    activeKey,
    isEditMode,
    isWorkflow,
    errorFields,
    isFormValid,
    setIsNewList,
    changeHandler,
    isActionModal,
    selectedListId,
    enableSaveButton,
    createNewListName,
    resetSelectedLists,
    loadingEmailUpdate,
    setCreateNewlistName,
    setResetSelectedLists,
  } = props;

  // Redux States
  const lists = useSelector((state: RootState) => state.automation.lists);
  const modal = useSelector((state: RootState) => state.automation.modals.addAction);
  const listsLoading = useSelector((state: RootState) => state.automation.lists.loading);
  const listData: IListData[] = useSelector((state: RootState) => state.automation.lists.data);
  const itemData = useSelector((state: RootState) => state.automation.workflow.flow[id]?.detail);
  const actionModalFrom = useSelector((state: RootState) => state.automation.modals.addAction.from);
  const triggerLists: IListData[] = useSelector((state: RootState) => state.automation.triggerLists.data);
  const triggerListId: number = useSelector((state: RootState) => state.automation.workflow.detail.listId);
  const triggerListsLoading: boolean = useSelector((state: RootState) => state.automation.triggerLists.loading);
  const triggerSearchTerm: string = useSelector((state: RootState) => state.automation.triggerLists.searchTerm);
  const newlyListCreated: IListData = useSelector((state: RootState) => state.automation.lists.newlyCreatedList);
  const actionModalVisible: boolean = useSelector((state: RootState) => state.automation.modals.addAction.visible);
  const workflowSearchSuccess: boolean = useSelector((state: RootState) => state.automation.userWorkflows.success); // For Alpha case in search
  const selectedListsInRedux: IListData[] = useSelector((state: RootState) => state.automation.lists.selectedLists);
  const selectedListIdsInRedux: number[] = useSelector((state: RootState) => state.automation.lists.selectedListIds);
  const newListCreatedError: boolean = useSelector((state: RootState) => state.automation.lists.newListCreatedError);
  const workflowSearchErrorMessage: string = useSelector((state: RootState) => state.automation.userWorkflows.message); // For Alpha case in search
  const newListCreatedSuccess: boolean = useSelector((state: RootState) => state.automation.lists.newListCreatedSuccess);
  const triggerSearchedLists: IListData[] = useSelector((state: RootState) => state.automation.triggerLists.searchedData);

  // Local States
  const [disableInput, setDisableInput] = useState(false);
  const [searchWord, setSearchWord] = useState<string>('');
  const [selectedLists, setSelectedLists] = useState<IListData[]>([]);
  const [selectedListIds, setSelectedListIds] = useState<number[]>([]);
  const [isSearchedList, setIsSearchedList] = useState<boolean>(false);
  const [selectedListNew, setSelectedListNew] = useState(selectedListId);

  // Local Variables
  let query = useQuery();
  const dispatch = useDispatch();
  const isActionNode: boolean = !(actionModalFrom === 'trigger' || !isWorkflow);
  const searchTerm: string = isActionNode ? searchWord : triggerSearchTerm;
  const isListModalOpen = modal.visible === true && modal.tab === ControlNameEnum.ADD_LIST;
  let dependencies = itemData && isListModalOpen;
  let listsData: IListData[] = isActionNode
    ? isSearchedList
      ? lists.searchedData
      : lists.data
    : isSearchedList
    ? triggerSearchedLists
    : triggerLists;

  // useRefs
  const audienceListRef = useRef<HTMLDivElement>(null);

  // Component Logic
  const getPagination = (reset?: boolean, searchValue?: string, resetSearchData?: boolean) => {
    let params: ParamsObjectType = {
      limit: isActionNode ? 15 : 20,
      offset: reset ? 0 : listsData.length,
    };
    if (searchValue) {
      params.list_name = searchValue;
    } else if (searchTerm) {
      if (searchValue !== '') params.search_term = searchTerm;
    }
    if (isActionNode) {
      if (reset) resetWorkflowLists();
      if (resetSearchData) params.reset = resetSearchData;
      if (isListModalOpen && itemData) {
        let listsIds = itemData?.list?.map((list: IListData) => list.list_id);
        params.workflow_list_ids = listsIds.join(',');
      }
    } else {
      if (isWorkflow) {
        params.workflow_id = query.get('workflow_id');
      }
      if (triggerListId) {
        params.trigger_list_id = triggerListId;
      }
      if ((isEditMode || actionModalFrom === 'trigger') && selectedListNew) {
        params.workflow_list_id = selectedListNew;
      }
    }
    return params;
  };

  const updateSearchTermInStore = (value: string) => {
    dispatch({
      type: AUTOMATION_LISTS_TRIGGER.SET_SEARCH_TERM,
      payload: value,
    });
    dispatch({ type: AUTOMATION_LISTS_TRIGGER.RESET_SEARCH_DATA });
  };

  const resetSelectedListsFunction = () => {
    setSelectedListIds([]);
    setSelectedLists([]);
    setSelectedlistIdsAction([]);
  };

  const checkboxHandler = (isChecked: boolean, clickedList: IListData) => {
    let checkedListIds: any = [];
    let checkedLists: IListData[] = [];
    if (isChecked) {
      checkedListIds = [...selectedListIds, clickedList?.list_id];
      checkedLists = [...selectedLists, clickedList];
    } else {
      checkedListIds = selectedListIds.filter((id) => id !== clickedList.list_id);
      checkedLists = selectedLists.filter((list) => list.list_id !== clickedList.list_id);
    }
    setSelectedListIds(checkedListIds);
    setSelectedLists(checkedLists);
    setSelectedlistIdsAction(checkedListIds);
    enableSaveButton(checkedListIds.length > 0);
  };

  const searchHandler = (value: string) => {
    if (isActionNode) {
      updateListSearchTermInStore(value);
      setSearchWord(value);
      setIsSearchedList(value.trim() !== '');
      if (!listsLoading && value.trim() !== '') listsAPI(getPagination(true, value));
      if (value.trim() === '') {
        listsAPI(getPagination(true));
      }
    } else {
      updateSearchTermInStore(value);
      setIsSearchedList(value.trim() !== '');
      if (!triggerListsLoading && value.trim() !== '') triggerListsAPI(getPagination(true, value));
      if (value.trim() === '') {
        triggerListsAPI(getPagination(true));
      }
    }
  };

  const resetSearchHandler = () => {
    if (isActionNode) {
      updateListSearchTermInStore('');
      setSearchWord('');
      setIsSearchedList(false);
      listsAPI(getPagination(true));
      store.dispatch({
        type: AUTOMATION_USER_WORKFLOWS.ERROR_SEARCH_TERM,
        payload: { message: '', success: true },
      });
    } else {
      if (isSearchedList) {
        if (audienceListRef.current) {
          audienceListRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        updateSearchTermInStore('');
        resetTriggerWorkflowLists();
        triggerListsAPI(getPagination(true));
      }
      store.dispatch({
        type: AUTOMATION_USER_WORKFLOWS.ERROR_SEARCH_TERM,
        payload: { message: '', success: true },
      });
      setIsSearchedList(false);
    }
  };

  const createNewListNameChangeHandler = (e: any) => {
    setCreateNewlistName(e.target.value);
  };

  const createNewListSaveHandler = (e: any) => {
    createNewListInWorkflow(createNewListName.trim(), isSearchedList, searchTerm);
    setDisableInput(true);
  };

  const createNewListCrossHandler = () => {
    setIsNewList(false);
    setCreateNewlistName('');
  };

  const handleNext = () => {
    if (isActionNode) {
      if (!listsLoading) listsAPI(getPagination(false, searchTerm));
    } else {
      if (!triggerListsLoading) {
        triggerListsAPI(getPagination(false, searchTerm));
      }
    }
  };

  const closeModal = () => {
    dispatch(addActionModal(false));
    dispatch(clearDropArea());
  };

  const submitHandler = (event: any) => {
    event.preventDefault();
    if (isActionNode) {
      let obj = findDropAreaData(ControlNameEnum.ADD_LIST);
      let selectedList: any = selectedLists.filter((list) => list.list_id !== null);
      let title = `: ${selectedList.map((list: IListData) => ` ${list.list_name}`)}`;
      if (id) {
        // edit Node
        dispatch(
          editNode(id, {
            ...itemData,
            list: selectedList,
            nodeTitle: title,
          }),
        );
      } else {
        dispatch(
          addNode({
            ...obj,
            list: selectedList,
            nodeTitle: title,
          }),
        );
      }
      resetSelectedListsFunction();
      enableSaveButton(false);
    } else {
      const workflowId = getQueryParam('workflow_id');
      const selectedList = listsData.filter((list: IListData) => list.list_id === selectedListId)[0];
      updateWorkflowTriggerList(parseInt(workflowId), selectedList.list_id, selectedList.list_name);
    }
    closeModal();
  };

  // useEffects
  useEffect(() => {
    if (isActionNode) {
      if (selectedListsInRedux?.length) {
        setSelectedListIds(selectedListIdsInRedux);
        setSelectedLists(selectedListsInRedux);
      } else {
        resetSelectedListsFunction();
      }
    } else {
      triggerListsAPI(getPagination(true));
    }
    return () => {
      const fromParam = getQueryParam('from');
      if (fromParam === 'listEdit' && id) setSelectedListsAction(selectedLists);
    };
  }, []);

  useEffect(() => {
    if (isActionNode) listsData = isSearchedList ? lists.searchedData : lists.data;
  }, [listData]);

  useEffect(() => {
    if (resetSelectedLists) {
      resetSelectedListsFunction();
      setResetSelectedLists(false);
    }
  }, [resetSelectedLists]);

  useEffect(() => {
    if (isActionNode) {
      if (modal.visible === false && selectedListIds.length > 0) {
        enableSaveButton(false);
        resetSelectedListsFunction();
      }
    }
  }, [modal]);

  useEffect(() => {
    if (activeKey === ControlNameEnum.ADD_LIST) {
      if (!actionModalVisible) {
        if (isActionNode) {
          resetSelectedListsFunction();
          setSelectedListsAction([]);
          if (newlyListCreated.list_id) {
            dispatch({
              type: AUTOMATION_LISTS.NEW_LIST_DATA_RESET,
            });
          }
        } else {
          setSelectedListNew(0);
        }
        searchTerm?.length && resetSearchHandler();
      } else {
        // Every time the modal opens
        if (actionModalFrom === 'trigger') {
          resetTriggerWorkflowLists();
          triggerListsAPI(getPagination(true));
        } else {
          listsAPI(getPagination(true));
        }
        if (actionModalFrom === 'trigger' && triggerListId) {
          changeHandler(triggerListId);
        }
      }
    }
  }, [actionModalVisible]);

  useEffect(() => {
    if (newListCreatedSuccess) {
      checkboxHandler(true, newlyListCreated); // Selecting the newly created list
      setTimeout(() => {
        dispatch({
          type: AUTOMATION_LISTS.CREATED_SUCCESS_STATE,
          payload: false,
        });
      }, 3000);
      setDisableInput(false);
      setIsNewList(false);
      setCreateNewlistName('');
      if (searchTerm.trim() !== '') {
        listsAPI(getPagination(true, searchTerm));
      } else {
        listsAPI(getPagination(true));
      }
      message.success('Your list has been added successfully!');
    }
  }, [newListCreatedSuccess]);

  useEffect(() => {
    if (newListCreatedError) {
      setTimeout(() => {
        dispatch({
          type: AUTOMATION_LISTS.CREATED_ERROR_STATE,
          payload: false,
        });
      }, 3000);
      setDisableInput(false);
      message.error('List already exists with that name!');
    }
  }, [newListCreatedError]);

  // useEffect to set the data of edit Node
  useEffect(() => {
    if (isActionNode) {
      if (isListModalOpen && itemData) {
        resetSelectedListsFunction();
        let ids = itemData?.list?.map((list: IListData) => list.list_id);
        if (ids?.length) {
          listsAPI(getPagination(true));
          setSelectedListIds(ids);
          setSelectedLists(itemData?.list);
          setSelectedlistIdsAction(ids);
        }
        enableSaveButton(ids?.length > 0);
      } else if (isListModalOpen && selectedListIds?.length > 0) {
        resetSelectedListsFunction();
        enableSaveButton(false);
      }
      if (isListModalOpen && listsData.length === 0) {
        handleNext();
        enableSaveButton(false);
      }
    }
  }, [dependencies, id]);

  const buildUI = () => {
    return (
      <InfiniteLoader
        dataLength={listsData.length}
        hasMore={true}
        next={handleNext}
        loader={triggerListsLoading || listsLoading ? <AutomationSkeleton /> : <></>}
        height="auto"
        scrollableTarget="automation-emails"
      >
        <Radio.Group
          onChange={(event) => {
            changeHandler(event.target.value);
          }}
          value={selectedListId}
        >
          <div ref={audienceListRef}>
            {listsData.length > 0 ? (
              listsData.map((list: IListData) => (
                <ListItem
                  list={list}
                  id={list.list_id}
                  key={list.list_id}
                  createdBy={list.created_by}
                  lastUsedBy={list.last_used_by}
                  totalContacts={list.total_contacts}
                  listName={list.list_name?.toString() ?? ''}
                  thumbnailFirst={list.thumbnails[0]?.length ? list.thumbnails[0] : DEFAULT_CAMPAIGN_IMAGE}
                  thumbnailSecond={list.thumbnails[1]?.length ? list.thumbnails[1] : DEFAULT_CAMPAIGN_IMAGE}
                  thumbnailThird={list.thumbnails[2]?.length ? list.thumbnails[2] : DEFAULT_CAMPAIGN_IMAGE}
                  thumbnailForth={list.thumbnails[3]?.length ? list.thumbnails[3] : DEFAULT_CAMPAIGN_IMAGE}
                  fromModal={isWorkflow}
                  isFormValid={isFormValid}
                  isActionNode={isActionNode}
                  isActionModal={isActionModal}
                  selectedListId={selectedListId}
                  checkboxHandler={checkboxHandler}
                  enableSaveButton={enableSaveButton}
                  loadingListUpdate={loadingEmailUpdate}
                />
              ))
            ) : !triggerListsLoading && !listsLoading ? (
              <EmptyTabInfo description={workflowSearchSuccess ? 'No Lists available.' : workflowSearchErrorMessage} />
            ) : null}
            {triggerListsLoading || listsLoading ? <AutomationSkeleton /> : null}
          </div>
        </Radio.Group>
      </InfiniteLoader>
    );
  };

  return (
    <Wrapper isActionModal={isActionModal} isActionNode={isActionNode} className={isActionModal ? 'w-100 wrapper-label audience-list' : ''}>
      <div className="wrapper-label d-flex justify-content-between">
        {!isActionNode ? (
          <div style={{ flex: 1 }}>
            <h3>Lists</h3>
            <span hidden={isWorkflow}>All lists will be displayed here:</span>
          </div>
        ) : null}
        <div style={{ flex: 1 }} className={`search-wrapper ${isActionModal ? 'action-modal' : ''}`}>
          <SentEmailSearch handleSearch={searchHandler} reset={resetSearchHandler} placeholder="Search lists" value="" />
        </div>
      </div>
      {isActionModal && (
        <Suspense fallback={<></>}>
          {errorFields && errorFields?.length > 0 ? (
            <div className="message-div">
              <Message
                smPadding
                label={''}
                cross={false}
                marginTop="14"
                type={'errorRed'}
                subMessage={`"${errorFields.join('" "')}"`}
                message={ALERT_MESSAGES.WORKFLOW_EMAILS_ACTION_ERROR}
              />
            </div>
          ) : (
            <div className="message-div">
              <Message message={ALERT_MESSAGES.WORKFLOW_EMAILS_ACTION} label="Heads up!" type="info" marginTop="14" cross={false} smPadding />
            </div>
          )}
        </Suspense>
      )}
      {isNewList ? (
        <li className="input-li">
          <InputWrapper>
            <Input
              type="text"
              disabled={disableInput}
              value={createNewListName}
              placeholder="Enter List Name"
              idInput="editable-input-list"
              onChange={createNewListNameChangeHandler}
            />
            <span
              onClick={createNewListSaveHandler}
              className={`save ${createNewListName?.trim()?.length === 0 ? 'disabled' : ''}`}
              data-qa="qa-save-name"
            >
              <img src={TickNormal} alt="save" />
            </span>
            <span onClick={createNewListCrossHandler} className={`cancel`} data-qa="qa-cancel-name">
              <img src={crossNormal} alt="cancel" />
            </span>
          </InputWrapper>
        </li>
      ) : null}
      {isWorkflow ? (
        <form
          id={`${ControlNameEnum.ADD_LIST}`}
          onSubmit={(e: any) => {
            submitHandler(e);
          }}
        >
          <div id="automation-emails" className={`scrollable-component ${isActionModal ? 'action-modal' : ''}`}>
            {buildUI()}
          </div>
        </form>
      ) : (
        <div id="automation-emails" className={`scrollable-component ${isActionModal ? 'action-modal' : ''}`}>
          {buildUI()}
        </div>
      )}
    </Wrapper>
  );
};

const defaultProps = {
  isActionModal: false,
};

AudienceList.defaultProps = defaultProps;

export default AudienceList;

const Wrapper = styled.div<{ isActionModal?: boolean; isActionNode?: boolean }>`
  width: 100%;
  max-height: calc(100vh - 290px);
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    width: 100%;
  }
  .message-div {
    padding: 0 10px 10px;
    margin-top: -5px;
    max-height: 65px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      max-height: 62px;
    }
  }
  .scrollable-component {
    overflow-y: hidden;
    .infinite-scroll-component {
      max-height: calc(100vh - 400px);
      overflow-y: auto !important;
      scrollbar-width: thin;
      scrollbar-color: #001760 #efefef;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        max-height: calc(100vh - 315px);
        height: 100vh;
      }
      &::-webkit-scrollbar {
        width: 5px;
        height: 5px;
        border-radius: 5px;
      }
      /* Track */
      &::-webkit-scrollbar-track {
        background: #fff;
        margin-top: 15px;
        margin-bottom: 15px;
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #001760;
      }
      /* Handle on hover */
      &::-webkit-scrollbar-thumb:hover {
        background: #01218a;
      }
    }
    &.action-modal {
      .infinite-scroll-component {
        max-height: 412px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          max-height: 331px;
        }
      }
    }
    .ant-radio-group {
      width: 100%;
      padding-top: 10px;
    }
  }

  .wrapper-label {
    width: 100%;
    position: sticky;
    top: 0;
    background: white;
    box-shadow: ${(props) => (props.isActionNode ? '0' : '0 2px 8px #e3e3e3')};
    z-index: 999;
    padding: 12px 23px 0px 16px;
    align-items: center;
    /* @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      padding: 12px 23px 12px 16px;
    } */
    .search-wrapper {
      margin-bottom: 0px;
      width: 250px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        width: 240px;
      }
      .search-wraper .search-bar {
        .search-input {
          height: 34px;
          margin-top: 2px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            height: 30px;
          }
        }
        .refresh-search {
          margin: 7px 8px 6px 8px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            margin: 8px 8px 6px 8px;
          }
        }
        .clear-search {
          margin-top: 8px;
        }
      }
      &.action-modal {
        width: 250px !important;
      }
    }
    h3 {
      color: #424b50;
      margin: ${(props) => (props.isActionModal ? '0' : '0 0 5px 0')};
      font-weight: 500;
      font-size: ${(props) => (props.isActionModal ? '18px' : '16px')};
      font-family: 'Roboto', sans-serif;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: ${(props) => (props.isActionModal ? '15px' : '13px')};
      }
    }
    span {
      font-family: 'Roboto', sans-serif;
      color: #949696;
      font-size: 14px;
      font-weight: 500;
      display: block;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 10px;
      }
    }
  }
`;

const InputWrapper = styled.div`
  position: relative;
  label {
    position: absolute;
    right: 0;
    top: -25px;
    margin: 0;
  }
  input {
    padding-right: 70px;
    margin-left: 16px;
    margin-top: 2px;
    margin-bottom: 2px;
    width: 95%;
    color: #424b50;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    font-family: 'Roboto';
  }
  .save {
    position: absolute;
    top: 10px;
    right: 40px;
    cursor: pointer;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      top: 5px;
    }
    img {
      vertical-align: middle;
      max-width: 20px;
    }
    &.disabled {
      pointer-events: none;
      img {
        opacity: 0.5;
      }
    }
  }
  .cancel {
    position: absolute;
    top: 10px;
    right: 70px;
    cursor: pointer;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      top: 5px;
    }
    img {
      vertical-align: middle;
      max-width: 20px;
      padding: 2px;
    }
  }
  .cdisable {
    pointer-events: none;
    opacity: 0.5;
  }
`;
